/* Minimal BCP-47 language tag helpers. Lowercasing is intentionally
 * locale-insensitive (toLowerCase, not toLocaleLowerCase): under a Turkish
 * locale, toLocaleLowerCase turns "I" into "ı" and breaks tag comparisons.
 * Subtags are split on "-" or "_" — some speech synthesis platforms report
 * voice languages as "en_US". */
export class LanguageTag {
  private static readonly memoized = new Map<string, LanguageTag>();

  static parse(tag: string): LanguageTag {
    let parsed = LanguageTag.memoized.get(tag);
    if (!parsed) {
      parsed = new LanguageTag(tag);
      LanguageTag.memoized.set(tag, parsed);
    }
    return parsed;
  }

  /** Primary language subtag: "en-US" → "en", "fil-PH" → "fil" */
  readonly primary: string;
  /** Script subtag (4 letters): "zh-Hans-CN" → "hans" */
  readonly script: string | undefined;
  /** Region subtag (2 letters or 3 digits): "zh-Hans-CN" → "cn",
   * "es-419" → "419" */
  readonly region: string | undefined;

  private constructor(tag: string) {
    const subtags = tag.toLowerCase().split(/[-_]/);
    this.primary = subtags[0];
    for (const subtag of subtags.slice(1)) {
      if (subtag.length === 1) break; // extension singleton: stop
      if (!this.script && /^[a-z]{4}$/.test(subtag)) this.script = subtag;
      else if (!this.region && /^([a-z]{2}|\d{3})$/.test(subtag))
        this.region = subtag;
    }
  }

  /** Strict language+region comparison, ignoring the script; an undefined
   * region must match a tag without one. */
  match(other: LanguageTag): boolean;
  match(primary: string, region: string | undefined): boolean;
  match(other: string | LanguageTag, region?: string | undefined): boolean {
    if (other instanceof LanguageTag) {
      if (this === other) return true;
      region = other.region;
      other = other.primary;
    }
    return this.primary === other && this.region === region;
  }
}
